import * as React from "react";
import { RouterLink as NextLink } from "../../router";
import type {
  DashboardPrimary,
  DashboardPrimaryBars,
  DashboardPrimaryLine,
  DashboardPrimaryTable,
} from "@excellent/core";
import { ListCard } from "../list-card";
import { DataTable } from "../data-table";
import { MetricChart } from "../metric/metric-chart";

/**
 * Renders the dashboard's primary visualization — a table or a bars list.
 * Sits beneath the stat grid, occupying the left/main column. Every
 * product declares this through its `dashboard.primary()` hook so the
 * five products read the same.
 */
export function DashboardPrimaryBlock({
  primary,
  fallbackTitle = "Synthesis",
  fallbackEmpty = "No data yet.",
}: {
  primary: DashboardPrimary | null;
  fallbackTitle?: string;
  fallbackEmpty?: string;
}) {
  if (!primary) {
    return (
      <ListCard title={fallbackTitle} count={0} empty={fallbackEmpty}>
        <></>
      </ListCard>
    );
  }
  if (primary.kind === "table") return <PrimaryTable data={primary} />;
  if (primary.kind === "line") return <PrimaryLine data={primary} />;
  return <PrimaryBars data={primary} />;
}

/** Shared "see all →" header link, used by every primary variant. */
function SeeAll({ href, label }: { href?: string; label?: string }) {
  if (!href || !label) return null;
  return (
    <NextLink
      href={href}
      className="type-body text-muted-foreground hover:text-foreground"
    >
      {label} →
    </NextLink>
  );
}

function PrimaryTable({ data }: { data: DashboardPrimaryTable }) {
  const seeAll = <SeeAll href={data.href} label={data.hrefLabel} />;

  return (
    <ListCard
      title={data.title}
      count={data.rows.length}
      action={seeAll}
      empty={data.emptyBody ?? "No rows yet."}
    >
      <DataTable
        wrapInCard={false}
        rows={data.rows}
        rowKey={(_row, i) => i}
        columns={data.columns.map((c) => ({
          key: c.key,
          header: c.label,
          width: c.width,
          align: c.align,
          render: (row: Record<string, unknown>) => {
            const v = row[c.key];
            const href = typeof row._href === "string" ? row._href : null;
            const display =
              v === null || v === undefined ? (
                <span className="text-muted-foreground">—</span>
              ) : (
                (v as React.ReactNode)
              );
            return href ? (
              <NextLink
                href={href}
                className="block hover:underline text-foreground"
              >
                {display}
              </NextLink>
            ) : (
              display
            );
          },
        }))}
      />
    </ListCard>
  );
}

const TONE_BAR_COLOR: Record<
  NonNullable<DashboardPrimaryBars["segments"][number]["tone"]>,
  string
> = {
  neutral: "var(--chart-1)",
  info: "var(--info)",
  warning: "var(--warning)",
  danger: "var(--danger)",
  success: "var(--success)",
};

function PrimaryBars({ data }: { data: DashboardPrimaryBars }) {
  const seeAll = <SeeAll href={data.href} label={data.hrefLabel} />;

  if (data.segments.length === 0) {
    return (
      <ListCard
        title={data.title}
        count={0}
        action={seeAll}
        empty={data.emptyBody ?? "No data yet."}
      >
        <></>
      </ListCard>
    );
  }

  const max = Math.max(...data.segments.map((s) => s.value)) || 1;
  return (
    <ListCard title={data.title} count={data.segments.length} action={seeAll}>
      <div className="flex flex-col">
        {data.segments.map((s, i) => {
          const pct = Math.max(2, (s.value / max) * 100);
          const color = TONE_BAR_COLOR[s.tone ?? "neutral"];
          return (
            <div
              key={s.label + i}
              className="flex items-center gap-3 border-b border-border last:border-b-0 even:bg-background/60 px-4 py-2 type-body"
            >
              <span className="w-32 truncate text-foreground">{s.label}</span>
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted/40">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${pct}%`, backgroundColor: color }}
                />
              </div>
              <span className="w-24 text-right tabular-nums text-muted-foreground">
                {s.display ?? s.value.toLocaleString()}
              </span>
            </div>
          );
        })}
      </div>
    </ListCard>
  );
}

/**
 * Trend line for a `kind:"line"` primary — the task-matched encoding for
 * movement over an ordered/temporal domain (cost over time, throughput per
 * day). Inline SVG only, mirroring StatCard's Sparkline: no Recharts on the
 * synthesis-dashboard critical path (dashboard-research.md P5/P8;
 * RESEARCH-CONVERGENCE-PLAN §4 "no new chart libraries on the synthesis
 * surface").
 *
 * Beyond a sparkline, this is a *primary* view: it labels the first/last
 * x-values and the latest y-value so the trend reads as values, not just a
 * shape (P9 details-on-demand), and carries an accessible first→last summary.
 * The stroke leads with the brand chart color; quantity is carried by
 * position/length only — no color-as-quantity (P12).
 */
function PrimaryLine({ data }: { data: DashboardPrimaryLine }) {
  const seeAll = <SeeAll href={data.href} label={data.hrefLabel} />;

  if (data.points.length === 0) {
    return (
      <ListCard
        title={data.title}
        count={0}
        action={seeAll}
        empty={data.emptyBody ?? "No data yet."}
      >
        <></>
      </ListCard>
    );
  }

  const first = data.points[0]!;
  const last = data.points[data.points.length - 1]!;
  const latestDisplay = last.display ?? last.value.toLocaleString();
  const trendWord =
    last.value > first.value ? "up" : last.value < first.value ? "down" : "flat";
  const summary = `${data.points.length}-point trend, ${first.label} to ${last.label}: ${
    first.display ?? first.value.toLocaleString()
  } → ${latestDisplay} (${trendWord})`;

  return (
    <ListCard
      title={data.title}
      action={
        <div className="flex items-center gap-3">
          <span className="type-body tabular-nums text-foreground">
            {latestDisplay}
            {data.valueLabel ? (
              <span className="text-muted-foreground"> {data.valueLabel}</span>
            ) : null}
          </span>
          {seeAll}
        </div>
      }
    >
      <div className="px-4 py-4">
        {/* Folded onto the shared MetricChart (../metric): a primary trend over an
            ordered domain — no reference line, no end-dot. The vertical guide at the
            latest point was dropped in the fold (the header already states the
            latest value); the first/last labels stay as composition. */}
        <MetricChart
          history={data.points.map((p) => p.value)}
          reference={{ mode: "none" }}
          verdict={{ intent: "neutral", label: summary }}
          showEndDot={false}
          className="h-36 w-full"
          ariaLabel={summary}
        />
        <div className="mt-2 flex items-center justify-between type-subtext text-muted-foreground tabular-nums">
          <span>{first.label}</span>
          <span>{last.label}</span>
        </div>
      </div>
    </ListCard>
  );
}
